import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtTime, fmtDate, todayISO } from "@/lib/utils-app";
import {
  CheckCircle2,
  XCircle,
  ChevronRight,
  ScanFace,
  Users,
  CalendarDays,
  MapPin,
  ShieldCheck,
  HardHat,
  Percent,
  Camera,
  Download,
  Navigation,
} from "lucide-react";
import { StatTile, StatStrip } from "@/components/StatTile";
import { ImageLightbox } from "@/components/ImageLightbox";
import WorkerGpsTrailDialog from "@/components/admin/WorkerGpsTrailDialog";

function istToday() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
}

export default function AttendanceRolePage({ role: roleProp }) {
  const params = useParams();
  const role = roleProp || (params.role === "contractor" ? "contractor_supervisor" : "pmc_supervisor");
  const isPmc = role === "pmc_supervisor";
  const roleLabel = isPmc ? "PMC Worker Captures" : "Contractor Worker Captures";
  const roleSubtitle = isPmc ? "Captures done by ITCOT supervisors" : "Captures done by contractor supervisors";
  const roleIcon = isPmc ? ShieldCheck : HardHat;
  const roleAccent = isPmc ? "sky" : "amber";

  const [parks, setParks] = useState([]);
  const [parkId, setParkId] = useState("");
  const [date, setDate] = useState(istToday());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { src, caption }
  const [statusFilter, setStatusFilter] = useState("all"); // all | present | absent
  const [gpsTrailOpen, setGpsTrailOpen] = useState(false);

  useEffect(() => {
    api.get("/parks").then((r) => {
      setParks(r.data);
      if (!parkId && r.data[0]) setParkId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!parkId || !date) return;
    setBusy(true);
    api
      .get("/admin/attendance/daily", { params: { park_id: parkId, date, role } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  }, [parkId, date, role]);

  const openDrill = async (emp) => {
    try {
      const { data } = await api.get("/admin/attendance/worker", {
        params: { employee_id: emp.employee_id, end_date: date, days: 7, role },
      });
      setDrill(data);
    } catch {
      // ignore
    }
  };

  const openSupervisorDrill = async (sup) => {
    try {
      const { data } = await api.get("/admin/supervisor-attendance/worker", {
        params: { supervisor_id: sup.supervisor_id, end_date: date, days: 7 },
      });
      setDrill({
        employee: {
          id: data.supervisor?.id,
          name: data.supervisor?.name,
          code: data.supervisor?.username,
        },
        from: data.from,
        to: data.to,
        captures: data.captures,
        isSupervisor: true,
      });
    } catch {
      // ignore
    }
  };

  // Reject a PMC supervisor's worker capture. Used when admin spots a
  // wrongly-captured attendance entry. The backend flips the row to
  // status=rejected which triggers the PMC's "Re-capture needed" flow.
  const onAdminRejectCapture = async (cap) => {
    if (!cap?.id) return;
    if (!window.confirm(
      `Reject this capture for ${cap.employee_name || drill?.employee?.name || "this employee"}?\n` +
      `The supervisor will be asked to re-capture.`
    )) return;
    try {
      await api.post(`/admin/attendance/captures/${cap.id}/reject`, {});
      toast.success("Capture rejected — supervisor can now re-capture");
      // Refresh the drill view by re-opening the worker
      const emp = drill?.employee;
      if (emp?.id) {
        const refreshed = await api.get("/admin/attendance/worker", {
          params: { employee_id: emp.id, end_date: date, days: 7, role },
        });
        setDrill(refreshed.data);
      }
      // Also refresh the daily summary so the row's "Match%" / status updates.
      if (parkId && date) {
        const r = await api.get("/admin/attendance/daily", {
          params: { park_id: parkId, date, role },
        });
        setData(r.data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to reject capture"));
    }
  };

  const park = parks.find((p) => p.id === parkId);
  const totals = data?.totals || { deployed: 0, present: 0, absent: 0 };
  const attendanceRate = totals.deployed > 0 ? Math.round((totals.present / totals.deployed) * 100) : 0;
  const isToday = date === todayISO();
  const parkSupervisors = data?.park_supervisors || [];

  const filteredRows = (data?.rows || []).filter((r) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "present") return r.captures.length > 0;
    if (statusFilter === "absent") return r.captures.length === 0;
    return true;
  });

  const exportXlsx = async () => {
    if (!parkId) return;
    try {
      const { tokenStore, API_BASE } = await import("@/lib/api");
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/admin/attendance/captures-export.xlsx?park_id=${parkId}&date=${date}&role=${role}`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const roleShort = role === "pmc_supervisor" ? "pmc" : "contractor";
      a.download = `${roleShort}_captures_${park?.code || "park"}_${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {}
  };

  return (
    <AppShell title={roleLabel} subtitle={roleSubtitle} action={
      <Button
        onClick={exportXlsx}
        disabled={!parkId}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="att-export-xlsx"
      >
        <Download className="mr-1.5 h-4 w-4" />
        Download Excel
      </Button>
    }>
      <div className="flex flex-col gap-4">
        {/* Hero filter bar */}
        <Card className="border-slate-200">
          <CardContent className="grid gap-3 p-4 md:grid-cols-12">
            <div className="md:col-span-5 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <MapPin className="mr-1 inline h-3 w-3" /> Park
              </Label>
              <Select value={parkId} onValueChange={setParkId}>
                <SelectTrigger data-testid="att-park-select" className="h-10">
                  <SelectValue placeholder="Select park" />
                </SelectTrigger>
                <SelectContent>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarDays className="mr-1 inline h-3 w-3" /> Date
              </Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date-input" className="h-10" />
            </div>
            <div className="md:col-span-4 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Show
              </Label>
              <div className="flex h-10 gap-1 rounded-md border bg-background p-1">
                {[
                  { v: "all", label: "All" },
                  { v: "present", label: "Present" },
                  { v: "absent", label: "Absent" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setStatusFilter(opt.v)}
                    className={`flex-1 rounded text-xs font-medium transition-colors ${
                      statusFilter === opt.v
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-secondary"
                    }`}
                    data-testid={`att-filter-${opt.v}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stat strip */}
        <StatStrip cols={5}>
          <StatTile icon={Users} accent="slate" label="Deployed" value={totals.deployed} hint={park?.name} testid="att-total-deployed" />
          <StatTile icon={CheckCircle2} accent="emerald" label="Present" value={totals.present} hint={totals.deployed > 0 ? `${attendanceRate}% attendance` : "—"} testid="att-total-present" />
          <StatTile
            icon={XCircle}
            accent="rose"
            label={isToday && totals.absent > 0 ? "Pending / Absent" : "Absent"}
            value={totals.absent}
            hint={isToday ? "Today — pending until window closes" : null}
            testid="att-total-absent"
          />
          <StatTile
            icon={roleIcon}
            accent={roleAccent}
            label={isPmc ? "ITCOT supervisors" : "Contractor supervisors"}
            value={parkSupervisors.length || "—"}
            hint={
              parkSupervisors.length === 0
                ? "None assigned"
                : parkSupervisors.length === 1
                  ? parkSupervisors[0].name
                  : parkSupervisors.map((s) => s.name).slice(0, 2).join(", ") + (parkSupervisors.length > 2 ? ` +${parkSupervisors.length - 2}` : "")
            }
            testid="att-total-source"
          />
          {/* GPS Trail tile — clickable. Opens a Leaflet dialog showing
             every worker-attendance capture as a numbered marker plotted
             at the GPS coordinates the supervisor's device recorded,
             with the park's exact geofence drawn as a sky-blue ring.
             Inside-the-fence markers are emerald; outside ones are rose. */}
          {(() => {
            const allCaps = (data?.rows || []).flatMap((r) => r.captures || []);
            const insideN = allCaps.filter((c) => c.inside_geofence === true).length;
            const outsideN = allCaps.filter((c) => c.inside_geofence === false).length;
            const total = allCaps.length;
            const hint = total === 0
              ? "No captures yet"
              : outsideN > 0
                ? `${insideN} inside · ${outsideN} outside`
                : `${insideN} inside geofence`;
            return (
              <StatTile
                icon={Navigation}
                accent={outsideN > 0 ? "rose" : "sky"}
                label="GPS Trail"
                value={total || "—"}
                hint={hint}
                testid="att-total-gps-trail"
                onClick={() => setGpsTrailOpen(true)}
              />
            );
          })()}
        </StatStrip>

        {/* Contractor supervisor selfies strip (for contractor view) */}
        {(() => {
          const supervisorSelfies = (data?.supervisor_selfies || []).filter(
            (s) => s.captures && s.captures.length > 0
          );
          if (supervisorSelfies.length === 0) return null;
          return (
            <Card className="border-amber-200 bg-amber-50/40" data-testid="att-supervisor-selfies">
              <CardContent className="p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-amber-800">
                  <HardHat className="h-3.5 w-3.5" /> Contractor supervisor selfies
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {supervisorSelfies.map((s) => (
                    <button
                      key={s.supervisor_id}
                      type="button"
                      onClick={() => openSupervisorDrill(s)}
                      className="flex items-center gap-3 rounded-md border bg-white/80 p-2 text-left transition-colors hover:bg-white"
                      data-testid={`att-supervisor-${s.supervisor_id}`}
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
                        {s.captures && s.captures[0] ? (
                          <img
                            src={s.captures[0].photo_b64?.startsWith("data:") ? s.captures[0].photo_b64 : `data:image/jpeg;base64,${s.captures[0].photo_b64}`}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <ScanFace className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{s.supervisor_name}</div>
                        <div className="text-xs text-muted-foreground">
                          <span className="font-mono">{s.username}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {s.captures.map((c) => (
                            <Badge key={c.id} className="bg-emerald-600 hover:bg-emerald-600 text-white">
                              <CheckCircle2 className="mr-1 h-3 w-3" />
                              {c.window_label || "Open"} · {fmtTime(c.created_at)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* Worker table (column view) */}
        {busy ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading workers…</CardContent></Card>
        ) : !data || filteredRows.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground">
              <Users className="h-8 w-8 opacity-40" />
              <div>{!data || data.rows.length === 0 ? "No employees deployed at this park." : `No ${statusFilter} workers for the current filter.`}</div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="att-rows">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                  <tr className="border-b border-slate-200">
                    <th className="whitespace-nowrap px-2 py-2 text-center">#</th>
                    <th className="whitespace-nowrap px-2 py-2 text-center">Photo</th>
                    <th className="whitespace-nowrap px-2 py-2 text-center">Code</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Name</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Category</th>
                    <th className="whitespace-nowrap px-3 py-2 text-center">Status</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Captures</th>
                    <th className="whitespace-nowrap px-2 py-2 text-center">Match %</th>
                    <th className="whitespace-nowrap px-2 py-2 text-center">Open</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, idx) => {
                    // Prefer the most-recent non-rejected capture for the row preview.
                    // Rejected captures (incl. PMC-rejected verifications) are still
                    // displayed in the Captures column for traceability.
                    const acceptedCaps = (r.captures || []).filter((c) => c.status !== "rejected");
                    const cap = acceptedCaps[0] || r.captures[0];
                    // PMC/admin "Mark Absent" overrides take precedence over the
                    // capture-based status — when set the row is firmly Absent.
                    const absentMarked = !!r.absent_marked;
                    const isPresent = !absentMarked && acceptedCaps.length > 0;
                    const isPendingToday = !isPresent && !absentMarked && isToday;
                    const pct = cap?.face_match_pct;
                    const rowCls = absentMarked
                      ? "bg-rose-100/70 hover:bg-rose-200/60 border-l-2 border-l-rose-500"
                      : !cap
                        ? (isPendingToday
                            ? "bg-amber-50/40 hover:bg-amber-100/40 border-l-2 border-l-amber-300"
                            : "bg-rose-50/60 hover:bg-rose-100/60 border-l-2 border-l-rose-300")
                        : !isPresent
                          ? "bg-rose-50/60 hover:bg-rose-100/60 border-l-2 border-l-rose-400"
                          : cap.face_status === "match"
                            ? "bg-emerald-50/40 hover:bg-emerald-100/50"
                            : cap.face_status === "mismatch"
                              ? "bg-rose-50/60 hover:bg-rose-100/60 border-l-2 border-l-rose-400"
                              : "bg-amber-50/50 hover:bg-amber-100/60";
                    // Row is off-schedule if ANY capture in the row was made
                    // at a park not on the supervisor's pinned schedule for
                    // that date. Pulses a soft amber tint + left border so
                    // admin spots the deviation at a glance.
                    const isOffSchedule = (r.captures || []).some((c) => c.off_schedule === true);
                    const offCls = isOffSchedule
                      ? " bg-amber-100/70 hover:bg-amber-200/70 border-l-4 border-l-amber-500 animate-pulse"
                      : "";
                    return (
                      <tr key={r.employee_id} className={`border-b border-slate-100 cursor-pointer ${rowCls}${offCls}`} onClick={() => openDrill(r)} data-testid={`att-row-${r.employee_code}`}
                        title={isOffSchedule ? "Off-schedule capture: park not on the supervisor's pinned schedule for this date" : undefined}
                      >
                        <td className="whitespace-nowrap px-2 py-2 text-center font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          <div className="relative mx-auto h-10 w-10 overflow-hidden rounded-md border bg-muted">
                            {cap?.photo_b64 ? (
                              <img src={cap.photo_b64.startsWith("data:") ? cap.photo_b64 : `data:image/jpeg;base64,${cap.photo_b64}`} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <ScanFace className="m-auto h-5 w-5 text-muted-foreground/60" />
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center"><span className="font-mono text-xs font-semibold text-slate-800">{r.employee_code}</span></td>
                        <td className="whitespace-nowrap px-3 py-2 text-left font-medium text-slate-900">{r.employee_name}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-left text-xs text-slate-700">{r.designation || "—"}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-center">
                          {absentMarked
                            ? <span
                                className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold text-white"
                                title={r.absent_marked_by ? `Marked Absent by ${r.absent_marked_by}` : "Marked Absent"}
                                data-testid={`att-row-${r.employee_code}-absent-marked`}
                              >
                                <XCircle className="h-3 w-3" />Absent
                                {r.absent_marked_by ? <span className="ml-0.5 font-normal opacity-90">· {r.absent_marked_by}</span> : null}
                              </span>
                            : !isPresent
                              ? (isPendingToday
                                  ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900" data-testid={`att-row-${r.employee_code}-pending`}><Camera className="h-3 w-3" />Pending</span>
                                  : <span className="inline-flex items-center gap-1 rounded-full bg-rose-300 px-1.5 py-0.5 text-[10px] font-semibold text-rose-950" data-testid={`att-row-${r.employee_code}-absent`}><XCircle className="h-3 w-3" />Absent</span>)
                              : cap?.face_status === "match"
                                ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-300 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-950"><CheckCircle2 className="h-3 w-3" />Match</span>
                                : cap?.face_status === "mismatch"
                                  ? <span className="inline-flex items-center gap-1 rounded-full bg-rose-300 px-1.5 py-0.5 text-[10px] font-semibold text-rose-950"><XCircle className="h-3 w-3" />Mismatch</span>
                                  : <span className="inline-flex items-center gap-1 rounded-full bg-amber-300 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950">Present</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-left">
                          {!cap ? <span className="text-xs text-slate-400">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {r.captures.map((c) => {
                                const isPmcVerified = c.source === "pmc_verified_contractor";
                                const isPmcRejected = isPmcVerified && c.status === "rejected";
                                return (
                                  <span
                                    key={c.id}
                                    className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                                      isPmcRejected
                                        ? "bg-rose-100 text-rose-800 ring-1 ring-rose-300"
                                        : isPmcVerified
                                          ? "bg-violet-100 text-violet-800 ring-1 ring-violet-300"
                                          : "bg-slate-100 text-slate-700"
                                    }`}
                                    data-testid={`att-row-${r.employee_code}-cap${isPmcVerified ? "-pmc-verified" : ""}`}
                                    title={
                                      isPmcRejected
                                        ? `PMC-rejected by ${c.captured_by_name || "—"}${c.pmc_rejection_reason ? ` — ${c.pmc_rejection_reason}` : ""}`
                                        : isPmcVerified
                                          ? `PMC-verified by ${c.captured_by_name || "—"}`
                                          : undefined
                                    }
                                  >
                                    {isPmcRejected
                                      ? <XCircle className="h-3 w-3" />
                                      : isPmcVerified
                                        ? <ShieldCheck className="h-3 w-3" />
                                        : <Camera className="h-3 w-3" />}
                                    {isPmcRejected ? "PMC-rejected" : isPmcVerified ? "PMC-verified" : (c.window_label || "Open")} &middot; {fmtTime(c.created_at)}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          {pct != null
                            ? <span className={`font-mono text-xs font-semibold tabular-nums ${pct >= 80 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-rose-700"}`}>{pct}%</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-center">
                          <ChevronRight className="mx-auto h-4 w-4 text-muted-foreground" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-emerald-100 ring-1 ring-emerald-300" />Match</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-100 ring-1 ring-amber-400" />Pending review</span>
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-rose-100 ring-1 ring-rose-400" />Absent / mismatch</span>
              <span className="ml-auto text-slate-500">Click any row for 7-day capture history</span>
            </div>
          </Card>
        )}
      </div>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{drill?.employee?.name || "Worker"} — last 7 days</DialogTitle>
            <div className="text-xs text-muted-foreground">
              {drill?.from && drill?.to ? <>{fmtDate(drill.from)} → {fmtDate(drill.to)}</> : null}
            </div>
          </DialogHeader>
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
            {(!drill?.captures || drill.captures.length === 0) && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No captures in this range.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="att-drill-grid">
              {(drill?.captures || []).map((c) => {
                const pct = c.face_match_pct;
                const lowMatch = pct != null && pct < 60;
                const isPmcVerified = c.source === "pmc_verified_contractor";
                const isPmcRejected = isPmcVerified && c.status === "rejected";
                // A "plain" rejection — the original supervisor's capture
                // was flipped to status=rejected by admin (this page). The
                // re-capture flow then auto-unlocks the supervisor's row.
                const isAdminRejected = !isPmcVerified && c.status === "rejected";
                // Reject is only allowed for TODAY's captures. Past days
                // are locked because re-capture cannot be done for a past
                // date — the supervisor can only fix today's roster.
                const todayIso = istToday();
                const isTodayCapture = c.date === todayIso;
                const canAdminReject = isPmc && !isPmcVerified && c.status !== "rejected" && isTodayCapture;
                const src = c.photo_b64
                  ? (c.photo_b64.startsWith("data:") ? c.photo_b64 : `data:image/jpeg;base64,${c.photo_b64}`)
                  : "";
                const hasPhoto = !!c.photo_b64;
                return (
                  <div
                    key={c.id}
                    className={`overflow-hidden rounded-lg border ${lowMatch ? "ring-1 ring-rose-300" : ""}`}
                    data-testid={`att-drill-card-${c.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => hasPhoto && setLightbox({
                        src,
                        caption: `${fmtDate(c.date)} · ${fmtTime(c.created_at)} · By ${c.captured_by_name || "—"}${pct != null ? ` · ${pct}%` : ""}`,
                      })}
                      disabled={!hasPhoto}
                      className={`group relative block aspect-[4/5] w-full bg-slate-900/95 ${hasPhoto ? "cursor-zoom-in" : "cursor-not-allowed"}`}
                      data-testid={`att-drill-img-${c.id}`}
                      aria-label="Open photo"
                    >
                      {hasPhoto ? (
                        <img
                          src={src}
                          alt=""
                          className="absolute inset-0 h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.01]"
                        />
                      ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center text-xs text-white/70">
                          <ScanFace className="h-6 w-6 opacity-70" />
                          <span>No photo on file</span>
                        </div>
                      )}
                      <div className="absolute left-2 top-2">
                        <Badge
                          className={
                            lowMatch
                              ? "bg-rose-600 hover:bg-rose-600 text-white"
                              : "bg-emerald-600 hover:bg-emerald-600 text-white"
                          }
                        >
                          <Percent className="mr-0.5 h-3 w-3" />
                          {pct == null ? "—" : `${pct}`}
                        </Badge>
                      </div>
                      <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
                        <Badge variant="secondary" className="opacity-90">{c.window_label || "Open"}</Badge>
                        {isPmcRejected ? (
                          <Badge className="bg-rose-600 text-white hover:bg-rose-600" data-testid={`att-drill-pmc-rejected-${c.id}`}>
                            <XCircle className="mr-1 h-3 w-3" /> PMC-rejected
                          </Badge>
                        ) : isAdminRejected ? (
                          <Badge className="bg-rose-600 text-white hover:bg-rose-600" data-testid={`att-drill-admin-rejected-${c.id}`}>
                            <XCircle className="mr-1 h-3 w-3" /> Admin-rejected
                          </Badge>
                        ) : isPmcVerified ? (
                          <Badge className="bg-violet-600 text-white hover:bg-violet-600" data-testid={`att-drill-pmc-verified-${c.id}`}>
                            <ShieldCheck className="mr-1 h-3 w-3" /> PMC-verified
                          </Badge>
                        ) : null}
                      </div>
                    </button>
                    <div className="p-2 text-xs">
                      <div className="font-medium">{fmtDate(c.date)} · {fmtTime(c.created_at)}</div>
                      <div className="text-muted-foreground">By {c.captured_by_name || "—"}</div>
                      {isPmcRejected && c.pmc_rejection_reason ? (
                        <div className="mt-1 rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-1 text-[10px] text-rose-800">
                          <span className="font-medium">Reason:</span> {c.pmc_rejection_reason}
                        </div>
                      ) : null}
                      {isAdminRejected ? (
                        <div className="mt-1 rounded-sm border border-rose-200 bg-rose-50 px-1.5 py-1 text-[10px] text-rose-800" data-testid={`att-drill-admin-reject-reason-${c.id}`}>
                          <span className="font-medium">Rejected{c.rejected_by_name ? ` by ${c.rejected_by_name}` : ""}.</span>
                          {c.pmc_rejection_reason ? <> {c.pmc_rejection_reason}</> : null}
                        </div>
                      ) : null}
                      {canAdminReject ? (
                        <div className="mt-1.5 flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                            onClick={() => onAdminRejectCapture(c)}
                            data-testid={`att-drill-reject-${c.id}`}
                          >
                            <XCircle className="mr-1 h-3.5 w-3.5" />
                            Reject
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setDrill(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      ) : null}

      {gpsTrailOpen ? (
        <WorkerGpsTrailDialog
          park={park}
          date={date}
          rows={data?.rows || []}
          onClose={() => setGpsTrailOpen(false)}
        />
      ) : null}
    </AppShell>
  );
}
